import type { MonthViewProps } from '../components/MonthView';
import type { CalendarEvent } from 'owa-calendar-types';
import { addMonths, asDate, isSameMonth } from 'owa-datetime';
import { createMonthDateRange, getDatesInDateRanges, createDateRange } from 'owa-datetime-utils';
import { useOrchestrator } from 'owa-react-hooks/lib/useOrchestrator';
import { navigateCalendarAssetToDate } from 'owa-timestream-asset-sdk-actions';
import { useGetCalendarEvents } from 'owa-timestream-asset-sdk-calendarevents';
import { getFolderIdByCalendarID } from 'owa-timestream-asset-sdk-calendars';
import * as React from 'react';
import {
    getFirstDayOfWeek,
    isDateOutOfMonthDayPickerRange,
    convertToOwaDate,
} from '../utils/dateUtils';

export default function useMonthViewViewState({
    selectedDate,
    onDateUpdated,
    calendarIds,
    contextMenuOpen,
}: MonthViewProps) {
    const [valueTop, setValueTop] = React.useState<Date>();
    const [valueBottom, setValueBottom] = React.useState<Date>();

    const [navigatedDate, setNavigatedDate] = React.useState<Date>(selectedDate);

    const key = React.useRef<number>(0);

    const refsTop = React.useRef<{ [key: string]: HTMLElement }>({});
    const refsBottom = React.useRef<{ [key: string]: HTMLElement }>({});

    // need to use a ref here rather than a state, since setState dispatch actions for valueTop/valueBottom
    // are async, therefore they don't update in time for us to condition properly in isDateOutOfMonthDayPickerRange()
    // in the onNavigateDateCallback
    const isFocusTop = React.useRef<boolean>(true);

    const [target, setTarget] = React.useState<HTMLElement | null>();
    const [isAgendaVisible, setIsAgendaVisible] = React.useState<boolean>(true);

    // this callback allows for keeping Fluent WeeklyDayPicker control in sync with
    // date changes from outside the control, such as:
    //d
    //   - changes to selected date triggered by global date nav
    //   - changes to selected date from invoking Today button in month view header
    //
    // when directly clicking on dates within the control, we want to open agenda callout
    // and therefore handle updates through a different set of callbacks
    const onDateNavigationFromOutside = React.useCallback((selectedDate: Date) => {
        // reset component state to reflect new value
        setNavigatedDate(selectedDate);
        setValueTop(selectedDate);
        setValueBottom(undefined);

        // change the key to reset Fluent component internal states
        key.current = key.current ^ 1;

        // clean up the old refs
        refsTop.current = {};
        refsBottom.current = {};
    }, []);

    useOrchestrator(
        navigateCalendarAssetToDate,
        actionMessage => {
            // selected-date change for asset state is handled at the asset level,
            // but we still need to handle additional state internal to month view here
            onDateNavigationFromOutside(asDate(actionMessage.date));
        },
        []
    );

    const switchToToday = React.useCallback(
        (date: Date) => {
            // trigger change to selected-date in asset state
            onDateUpdated(date);
            // handle additional state internal to month view
            onDateNavigationFromOutside(date);
        },
        [onDateUpdated, onDateNavigationFromOutside]
    );

    const { getCalendarEventsForDateRange } = useGetCalendarEvents();

    const lowerMonthNavigatedDate = asDate(addMonths(convertToOwaDate(navigatedDate), 1));
    const firstDayOfWeek = getFirstDayOfWeek();

    const startOfMonthView = createMonthDateRange(convertToOwaDate(navigatedDate), firstDayOfWeek)
        .start;
    const endOfMonthView = createMonthDateRange(
        convertToOwaDate(lowerMonthNavigatedDate),
        firstDayOfWeek
    ).end;

    // The number 42 is used here because it is currently max amount of days that the top Month can have display.
    const topMonthDateRange = createDateRange(startOfMonthView, 42);
    const bottomMonthDateRange = { start: topMonthDateRange.end, end: endOfMonthView };

    const events = calendarIds
        .map(calendarId => getFolderIdByCalendarID(calendarId))
        .filter(x => x)
        .reduce((acc, folderId) => {
            acc.concat(getCalendarEventsForDateRange(folderId!, topMonthDateRange) || []);
            acc.concat(getCalendarEventsForDateRange(folderId!, bottomMonthDateRange) || []);
            return acc;
        }, [] as CalendarEvent[]);

    const getMarkedDays = (startingDate: Date, endingDate: Date) => {
        const startingOwaDate = convertToOwaDate(startingDate);
        const endingOwaDate = convertToOwaDate(endingDate);
        return getDatesInDateRanges(
            startingOwaDate,
            endingOwaDate,
            events.map(event => ({ start: event.Start, end: event.End }))
        ).map(d => asDate(d));
    };

    const onSelectDateCallbackTop = React.useCallback((date: Date, dates?: Date[]) => {
        setValueTop(date);
        setValueBottom(undefined);
        const ref = refsTop.current[date.toString()];
        setIsAgendaVisible(true);
        setTarget(ref);
        isFocusTop.current = true;
    }, []);

    const onSelectDateCallbackBottom = React.useCallback((date: Date, dates?: Date[]) => {
        setValueTop(undefined);
        setValueBottom(date);
        const ref = refsBottom.current[date.toString()];
        setIsAgendaVisible(true);
        setTarget(ref);
        isFocusTop.current = false;
    }, []);

    const onCalloutDismiss = React.useCallback(() => {
        if (!contextMenuOpen) {
            setTarget(null);
        }
    }, [contextMenuOpen]);

    const onNavigateDateCallback = React.useCallback(
        (date: Date, shouldFocus: boolean) => {
            const owaDate = convertToOwaDate(date);
            const owaNavigatedDate = convertToOwaDate(navigatedDate);
            // shouldFocus = true when navigation comes from clicking on a day or using keyboard rather than on the
            // navigation arrows. When clicking on a day thats shown in the month view but isn't in current navigated month
            // we don't want to navigate, isDateOutOfMonthDayPickerRange() = true when using keypad and we should navigate
            //
            if (
                !isSameMonth(owaNavigatedDate, owaDate) &&
                (!shouldFocus ||
                    isDateOutOfMonthDayPickerRange(owaNavigatedDate, owaDate, isFocusTop.current))
            ) {
                setNavigatedDate(date);

                // clean up the old refs
                refsTop.current = {};
                refsBottom.current = {};
            }
            onDateUpdated(date);
        },
        [navigatedDate, onDateUpdated]
    );

    const customDayCellRefTop = (element: HTMLElement, date: Date) => {
        refsTop.current[date.toString()] = element;
    };

    const customDayCellRefBottom = (element: HTMLElement, date: Date) => {
        refsBottom.current[date.toString()] = element;
    };

    return {
        weeklyDayPickerProps: {
            top: {
                onSelectDate: onSelectDateCallbackTop,

                customDayCellRef: customDayCellRefTop,
                getMarkedDays,
                firstDayOfWeek,
            },
            bottom: {
                onSelectDate: onSelectDateCallbackBottom,
                customDayCellRef: customDayCellRefBottom,
                getMarkedDays,
                firstDayOfWeek,
            },
        },
        // cast is due to Fabric WeeklyDayPicker component arbitrarily removing the shouldFocus param from the
        // type, but we need it to determine whether navigation came from arrow or clicking on date. fix needed in Fabric
        onNavigateDate: onNavigateDateCallback as (date: Date) => void,
        onCalloutDismiss,
        valueTop,
        valueBottom,
        navigatedDate,
        lowerMonthNavigatedDate,
        target,
        key: key.current,
        isAgendaVisible,
        setIsAgendaVisible,
        switchToToday,
    };
}
